import React, { useState, useEffect } from 'react';
import "../../App.css"
import { Table, Tag, Button, Modal, message, Input, Select, DatePicker, Radio, InputNumber, Form, Row, Col, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import { getAllOrders, deliverOrder, markOrderAsPaid, Order, updateOrderZone, updateOrder } from '../../services/orderService';
import { generateOrderPDF } from '../../services/pdfService';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { deleteOrder } from '../../services/orderService';
import { zoneService, Zone } from '../../services/zoneService';
import { productService, Product } from '../../services/productService';
import { getLotSize } from '../../utils/lotSize';

const { Option } = Select;

const normalizePhone = (value: string) => (value || '').replace(/\D/g, '');

interface EditItem {
    product: string;
    name: string;
    quantity: number;
    price: number;
    image: string;
    lotSize?: number;
}

const OrderManagement: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [zones, setZones] = useState<Zone[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().startOf('day'), dayjs().endOf('day')]);
    const [selectedPeriod, setSelectedPeriod] = useState<string>('today');
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [selectedOrderIdForPayment, setSelectedOrderIdForPayment] = useState<string | null>(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'cash' | 'orange_money' | 'wave'>('cash');
    const [zoneModalVisible, setZoneModalVisible] = useState(false);
    const [selectedOrderForZone, setSelectedOrderForZone] = useState<Order | null>(null);
    const [selectedZoneId, setSelectedZoneId] = useState<string | undefined>(undefined);
    const [zoneLoading, setZoneLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingOrder, setEditingOrder] = useState<Order | null>(null);
    const [editItems, setEditItems] = useState<EditItem[]>([]);
    const [editSaving, setEditSaving] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [productToAdd, setProductToAdd] = useState<string | undefined>(undefined);
    const [editForm] = Form.useForm();
    const watchedZoneId = Form.useWatch('zoneId', editForm);

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userType = user.role === 'user' ? 'agent' : user.role;

    useEffect(() => {
        fetchOrders();
        fetchZones();
        if (userType === 'admin') {
            fetchProducts();
        }
    }, [dateRange]);

    const fetchZones = async () => {
        try {
            const data = await zoneService.getAllZones();
            setZones(data);
        } catch (error) {
            console.error('Erreur lors du chargement des zones');
        }
    };

    const fetchProducts = async () => {
        try {
            const data = await productService.getAllProducts();
            setProducts(data);
        } catch (error) {
            console.error('Erreur lors du chargement des produits');
        }
    };

    const fetchOrders = async () => {
        try {
            const { orders: data } = await getAllOrders({
                startDate: dateRange[0].toISOString(),
                endDate: dateRange[1].toISOString(),
                limit: 10000,
            });
            setOrders(data);
        } catch (error) {
            message.error('Erreur lors de la récupération des commandes');
        } finally {
            setLoading(false);
        }
    };

        const { confirm } = Modal;

        const handleDeleteOrder = (orderId: string) => {
            confirm({
                title: 'Êtes-vous sûr de vouloir supprimer cette commande ?',
                icon: <ExclamationCircleOutlined />,
                content: 'Cette action est irréversible.',
                okText: 'Oui',
                okType: 'danger',
                cancelText: 'Non',
                onOk: async () => {
                    try {
                        await deleteOrder(orderId);
                        message.success('Commande supprimée avec succès');
                        fetchOrders();
                    } catch (error) {
                        message.error('Erreur lors de la suppression de la commande');
                    }
                },
            });
        };
    const handleOpenPaymentModal = (orderId: string) => {
        setSelectedOrderIdForPayment(orderId);
        setSelectedPaymentMethod('cash');
        setPaymentModalVisible(true);
    };

    const handleConfirmPayment = async () => {
        if (!selectedOrderIdForPayment) {
            setPaymentModalVisible(false);
            return;
        }
        try {
            await markOrderAsPaid(selectedOrderIdForPayment, selectedPaymentMethod);
            message.success('Commande marquée comme payée');
            setPaymentModalVisible(false);
            setSelectedOrderIdForPayment(null);
            fetchOrders();
        } catch (error) {
            message.error('Erreur lors de la mise à jour du statut de paiement');
        }
    };

    // Ancienne fonction handleMarkAsPaid conservée si besoin ailleurs
    const handleMarkAsPaid = async (orderId: string) => {
        handleOpenPaymentModal(orderId);
    };

    const handleOpenZoneModal = (order: Order) => {
        setSelectedOrderForZone(order);
        setSelectedZoneId(order.deliveryZone?.zone || undefined);
        setZoneModalVisible(true);
    };

    const handleConfirmZoneUpdate = async () => {
        if (!selectedOrderForZone) {
            setZoneModalVisible(false);
            return;
        }
        try {
            setZoneLoading(true);
            await updateOrderZone(selectedOrderForZone._id, selectedZoneId || null);
            message.success('Zone de livraison mise à jour');
            setZoneModalVisible(false);
            setSelectedOrderForZone(null);
            setSelectedZoneId(undefined);
            fetchOrders();
        } catch (error) {
            message.error('Erreur lors de la mise à jour de la zone');
        } finally {
            setZoneLoading(false);
        }
    };

    const handlePrintTicket = (order: Order) => {
        try {
            console.log(order);
            generateOrderPDF(order);
            message.success('PDF généré avec succès');
        } catch (error) {
            message.error('Erreur lors de la génération du PDF');
        }
    };

    const getProductId = (product: any) =>
        typeof product === 'object' && product !== null ? product._id : product;

    const handleOpenEditModal = (order: Order) => {
        setEditingOrder(order);
        setEditItems(
            order.orderItems.map((item) => ({
                product: getProductId(item.product),
                name: item.name,
                quantity: item.quantity,
                price: item.price,
                image: item.image,
                lotSize: getLotSize(item.price, item.lotSize),
            }))
        );
        editForm.setFieldsValue({
            firstName: order.personalInfo?.firstName || '',
            lastName: order.personalInfo?.lastName || '',
            phone: order.personalInfo?.phone || '',
            email: order.personalInfo?.email || '',
            zoneId: order.deliveryZone?.zone || undefined,
        });
        setProductToAdd(undefined);
        setEditModalVisible(true);
    };

    const handleEditItemQty = (productId: string, quantity: number) => {
        if (quantity <= 0) {
            setEditItems((prev) => prev.filter((item) => item.product !== productId));
            return;
        }
        setEditItems((prev) =>
            prev.map((item) => (item.product === productId ? { ...item, quantity } : item))
        );
    };

    const handleAddProductToEdit = () => {
        if (!productToAdd) return;
        const product = products.find((p) => p._id === productToAdd);
        if (!product) return;
        const existing = editItems.find((item) => item.product === product._id);
        if (existing) {
            setEditItems((prev) =>
                prev.map((item) =>
                    item.product === product._id ? { ...item, quantity: item.quantity + 1 } : item
                )
            );
        } else {
            setEditItems((prev) => [
                ...prev,
                {
                    product: product._id,
                    name: product.name,
                    quantity: 1,
                    price: product.price,
                    image: product.images?.[0] || '',
                    lotSize: getLotSize(product.price, product.lotSize),
                },
            ]);
        }
        setProductToAdd(undefined);
    };

    const getEditItemsPrice = () =>
        editItems.reduce((sum, item) => sum + item.price * item.quantity * getLotSize(item.price, item.lotSize), 0);

    const getEditShippingPrice = (zoneId?: string) => {
        if (!zoneId) return 0;
        return zones.find((z) => z._id === zoneId)?.price || 0;
    };

    const handleConfirmEdit = async () => {
        if (!editingOrder) return;
        if (editItems.length === 0) {
            message.error('La commande doit contenir au moins un produit');
            return;
        }
        try {
            const values = await editForm.validateFields();
            setEditSaving(true);
            await updateOrder(editingOrder._id, {
                personalInfo: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    phone: values.phone,
                    email: values.email,
                },
                orderItems: editItems.map((item) => ({
                    product: item.product,
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price,
                    image: item.image,
                })),
                zoneId: values.zoneId || null,
            });
            message.success('Commande mise à jour');
            setEditModalVisible(false);
            setEditingOrder(null);
            fetchOrders();
        } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.response?.data?.message || 'Erreur lors de la modification');
        } finally {
            setEditSaving(false);
        }
    };

    const handlePeriodChange = (period: string) => {
        setSelectedPeriod(period);
        let newDateRange: [dayjs.Dayjs, dayjs.Dayjs];
        
        switch (period) {
            case 'today':
                newDateRange = [dayjs().startOf('day'), dayjs().endOf('day')];
                break;
            case 'last7days':
                newDateRange = [dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')];
                break;
            case 'last30days':
                newDateRange = [dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')];
                break;
            default:
                newDateRange = [dayjs().startOf('day'), dayjs().endOf('day')];
        }
        
        setDateRange(newDateRange);
    };

    const handleDateRangeChange = (dates: any) => {
        if (dates && dates.length === 2) {
            setDateRange([dates[0].startOf('day'), dates[1].endOf('day')]);
            setSelectedPeriod('custom');
        }
    };

    // Filtrage client (prénom, nom, téléphone) et statut livraison
    const filteredOrders = orders.filter((order: any) => {
        const q = searchTerm.trim().toLowerCase();
        const fullName = `${order.personalInfo?.firstName || ''} ${order.personalInfo?.lastName || ''}`
            .toLowerCase()
            .trim();
        const phone = (order.personalInfo?.phone || '').toLowerCase();
        const phoneDigits = normalizePhone(order.personalInfo?.phone || '');
        const queryDigits = normalizePhone(q);
        const matchesSearch =
            !q ||
            fullName.includes(q) ||
            phone.includes(q) ||
            (!!queryDigits && phoneDigits.includes(queryDigits));
        const matchesStatus = !statusFilter || (statusFilter === 'delivered' ? order.isDelivered : !order.isDelivered);
        return matchesSearch && matchesStatus;
    });

    const columns = [
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleDateString(),
        },
        {
            title: 'Client',
            dataIndex: 'personalInfo',
            key: 'personalInfo',
            render: (personalInfo: any) => personalInfo?.firstName   || 'N/A',
        },
        {
            title: 'Numero Telephone',
            dataIndex: 'personalInfo',
            key: 'personalInfo',
            render: (personalInfo: any) => personalInfo?.phone || 'N/A',
        },
        {
            title: 'Total',
            dataIndex: 'totalPrice',
            key: 'totalPrice',
            render: (price: number) => `${price.toLocaleString('fr-FR')} FCFA`,
        },
        {
            title: 'Zone',
            key: 'deliveryZone',
            width: 110,
            render: (_: any, record: Order) =>
                record.deliveryZone?.name ? (
                    <Tag
                        color="blue"
                        style={{
                            maxWidth: 100,
                            margin: 0,
                            whiteSpace: 'normal',
                            fontSize: 10,
                            lineHeight: '14px',
                            textAlign: 'center',
                        }}
                    >
                        {record.deliveryZone.name}
                        <br />
                        {record.deliveryZone.price.toLocaleString('fr-FR')} F
                    </Tag>
                ) : (
                    <Tag style={{ margin: 0, fontSize: 10 }}>Retrait</Tag>
                ),
        },
        {
            title: 'Statut Paiement',
            dataIndex: 'isPaid',
            key: 'isPaid',
            render: (isPaid: boolean) => (
                <Tag color={isPaid ? 'green' : 'red'}>
                    {isPaid ? 'Payé' : 'Non payé'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Order) => (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Button 
                        type="primary" 
                        onClick={() => handlePrintTicket(record)}
                        size="small"
                    >
                        Imprimer
                    </Button>
                    {userType === 'admin' && (
                        <Button
                            type="default"
                            onClick={() => handleOpenEditModal(record)}
                            size="small"
                        >
                            Modifier
                        </Button>
                    )}
                    {userType === 'admin' && (
                        <Button
                            type="default"
                            onClick={() => handleOpenZoneModal(record)}
                            size="small"
                        >
                            Zone
                        </Button>
                    )}
                    {!record.isPaid && (
                        <Button 
                            type="default" 
                            onClick={() => handleOpenPaymentModal(record._id)}
                            size="small"
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                        >
                            Marquer payé
                        </Button>
                    )}
                    {userType === 'agent' && !record.isDelivered && (
                        <Button 
                            type="default" 
                            onClick={async () => {
                                try {
                                    await deliverOrder(record._id);
                                    message.success('Commande marquée comme livrée');
                                    fetchOrders();
                                } catch (error) {
                                    message.error('Erreur lors de la mise à jour de la commande');
                                }
                            }}
                            size="small"
                        >
                            Livrer
                        </Button>
                    )} 
                    {userType === 'admin' && (
                        <Button 
                            type="default" 
                            danger
                            onClick={() => handleDeleteOrder(record._id)}
                            size="small"
                        >
                            Annuler
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div>
            <div style={{ 
                display: 'flex', 
                gap: 16, 
                marginBottom: 16,
                flexWrap: 'wrap',
                alignItems: 'center'
            }}>
                <Input
                    placeholder="Nom, prénom ou téléphone"
                    title="Recherche parmi les commandes de la période sélectionnée."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={{ width: 320 }}
                    allowClear
                />
                <Select
                    placeholder="Filtrer par statut livraison"
                    value={statusFilter}
                    onChange={value => setStatusFilter(value)}
                    allowClear
                    style={{ width: 200 }}
                >
                    <Option value="">Tous les statuts</Option>
                    <Option value="delivered">Livré</Option>
                    <Option value="pending">En cours</Option>
                </Select>
                
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginLeft: 'auto'
                }}>
                    <Button 
                        type={selectedPeriod === 'today' ? 'primary' : 'default'}
                        onClick={() => handlePeriodChange('today')}
                        size="small"
                    >
                        Aujourd'hui
                    </Button>
                    <Button 
                        type={selectedPeriod === 'last7days' ? 'primary' : 'default'}
                        onClick={() => handlePeriodChange('last7days')}
                        size="small"
                    >
                        7 derniers jours
                    </Button>
                    <Button 
                        type={selectedPeriod === 'last30days' ? 'primary' : 'default'}
                        onClick={() => handlePeriodChange('last30days')}
                        size="small"
                    >
                        30 derniers jours
                    </Button>
                    <DatePicker.RangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        format="DD MMM YYYY"
                        placeholder={['Date début', 'Date fin']}
                        size="small"
                        style={{ width: '280px' }}
                    />
                </div>
            </div>
            
            <Table
    columns={columns}
    dataSource={filteredOrders}
    rowKey="_id"
    loading={loading}
    scroll={{ x: 'max-content' }}
    rowClassName={(record) => {
        if (record.isPaid) {
            return 'row-paid';     // vert
        } else {
            return 'row-unpaid';   // rouge clair
        }
    }}
    expandable={{
        expandedRowRender: (record) => (
            <div>
                <h4>Détails de la commande</h4>
                <p>
                    Zone de livraison:{" "}
                    {record.deliveryZone?.name
                        ? `${record.deliveryZone.name} (${record.deliveryZone.price.toLocaleString("fr-FR")} FCFA)`
                        : "Aucune (retrait magasin)"}
                </p>
                <p>
                    Adresse de livraison:{" "}
                    {record.shippingPrice == 0
                        ? "Retrait Magasin"
                        : record.shippingAddress?.city || record.deliveryZone?.name || "—"}
                </p>
                <p>Méthode de paiement: {record.paymentMethod}</p>
                <h4>Produits commandés:</h4>
                <ul>
                    {record.orderItems.map((item, index) => (
                        <li key={index}>
                            {item.name} - {item.quantity} x{" "}
                            {item.price.toLocaleString("fr-FR")} FCFA
                        </li>
                    ))}
                </ul>
            </div>
        ),
    }}
/>
        {/* Modal de sélection du type de paiement pour le marquage payé */}
        <Modal
            title="Type de paiement"
            open={paymentModalVisible}
            onOk={handleConfirmPayment}
            onCancel={() => setPaymentModalVisible(false)}
            okText="Confirmer le paiement"
            cancelText="Annuler"
        >
            <Radio.Group
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                value={selectedPaymentMethod}
            >
                <Radio value="cash">Espèces</Radio>
                <Radio value="orange_money">Orange Money</Radio>
                <Radio value="wave">Wave</Radio>
            </Radio.Group>
        </Modal>

        <Modal
            title="Modifier la zone de livraison"
            open={zoneModalVisible}
            onOk={handleConfirmZoneUpdate}
            onCancel={() => {
                setZoneModalVisible(false);
                setSelectedOrderForZone(null);
                setSelectedZoneId(undefined);
            }}
            confirmLoading={zoneLoading}
            okText="Enregistrer"
            cancelText="Annuler"
        >
            <p style={{ marginBottom: 12 }}>
                Commande: {selectedOrderForZone?.personalInfo?.firstName} {selectedOrderForZone?.personalInfo?.lastName}
            </p>
            <Select
                showSearch
                allowClear
                placeholder="Rechercher une zone..."
                value={selectedZoneId}
                onChange={(value) => setSelectedZoneId(value)}
                style={{ width: '100%' }}
                optionFilterProp="label"
                filterOption={(input, option) =>
                    String(option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                }
                options={zones.map(zone => ({
                    value: zone._id,
                    label: `${zone.name} — ${zone.price.toLocaleString('fr-FR')} FCFA`,
                }))}
            />
            <p style={{ marginTop: 12, color: '#666', fontSize: 13 }}>
                Le total de la commande sera recalculé avec le nouveau prix de livraison.
            </p>
        </Modal>

        <Modal
            title="Modifier la commande"
            open={editModalVisible}
            onOk={handleConfirmEdit}
            onCancel={() => {
                setEditModalVisible(false);
                setEditingOrder(null);
            }}
            confirmLoading={editSaving}
            okText="Enregistrer"
            cancelText="Annuler"
            width={720}
            destroyOnClose
        >
            {editingOrder && (editingOrder.isPaid || editingOrder.isDelivered) && (
                <p style={{ color: '#d46b08', marginBottom: 12 }}>
                    Cette commande est {editingOrder.isPaid ? 'payée' : ''}{editingOrder.isPaid && editingOrder.isDelivered ? ' et ' : ''}{editingOrder.isDelivered ? 'livrée' : ''}. Les totaux seront recalculés. Si elle est payée, le stock sera ajusté.
                </p>
            )}
            <Form form={editForm} layout="vertical">
                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name="firstName" label="Prénom" rules={[{ required: true, message: 'Prénom requis' }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="lastName" label="Nom" rules={[{ required: true, message: 'Nom requis' }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name="phone" label="Téléphone" rules={[{ required: true, message: 'Téléphone requis' }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="email" label="Email">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="zoneId" label="Zone de livraison">
                    <Select
                        showSearch
                        allowClear
                        placeholder="Aucune (retrait magasin)"
                        optionFilterProp="label"
                        options={zones.map((zone) => ({
                            value: zone._id,
                            label: `${zone.name} — ${zone.price.toLocaleString('fr-FR')} FCFA`,
                        }))}
                    />
                </Form.Item>
            </Form>

            <h4>Produits</h4>
            {editItems.map((item) => (
                <div key={item.product} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ flex: 1 }}>{item.name}</span>
                    <InputNumber
                        min={1}
                        value={item.quantity}
                        onChange={(value) => handleEditItemQty(item.product, value || 1)}
                        size="small"
                    />
                    <span style={{ width: 110, textAlign: 'right' }}>
                        {(item.price * item.quantity * getLotSize(item.price, item.lotSize)).toLocaleString('fr-FR')} F
                    </span>
                    <Popconfirm
                        title="Retirer ce produit de la commande ?"
                        okText="Retirer"
                        cancelText="Annuler"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleEditItemQty(item.product, 0)}
                    >
                        <Button type="link" danger size="small">
                            Retirer
                        </Button>
                    </Popconfirm>
                </div>
            ))}

            <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 16 }}>
                <Select
                    showSearch
                    placeholder="Ajouter un produit"
                    value={productToAdd}
                    onChange={(value) => setProductToAdd(value)}
                    style={{ flex: 1 }}
                    optionFilterProp="label"
                    options={products.map((p) => ({
                        value: p._id,
                        label: `${p.name} — ${p.price.toLocaleString('fr-FR')} FCFA`,
                    }))}
                />
                <Button onClick={handleAddProductToEdit} disabled={!productToAdd}>
                    Ajouter
                </Button>
            </div>

            <div style={{ textAlign: 'right' }}>
                <div>Sous-total: {getEditItemsPrice().toLocaleString('fr-FR')} FCFA</div>
                <div>Livraison: {getEditShippingPrice(watchedZoneId).toLocaleString('fr-FR')} FCFA</div>
                <div>
                    <strong>
                        Total: {(getEditItemsPrice() + getEditShippingPrice(watchedZoneId)).toLocaleString('fr-FR')} FCFA
                    </strong>
                </div>
            </div>
        </Modal>
        </div>
    );
};

export default OrderManagement; 